import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Checkbox, Divider, LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Text from '@/Components/inputs/Text';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import { matchSorter } from 'match-sorter';
import type { z } from 'zod';
import type { FilterSearchSchema } from '../registry';

import './DropdownMenu.less';

export type DropDownMenuItemProps = {
  name: string;
  onClick?: (state: DropdownMenuItemState, menuRef: HTMLDivElement | null) => void;
  icon?: React.JSX.Element;
  divider?: boolean;
  disabled?: boolean;
  /**
   * initial value when multiple select is enabled
   */
  initValue?: boolean;
  /**
   * the exact value of the item
   */
  value?: unknown;
  customClass?: string;
};

// either name or icon is required
type DropdownCombinationType =
  | {
      name?: string;
      icon: React.JSX.Element;
    }
  | {
      name: string;
      icon?: React.JSX.Element;
    };

type DropDownMenuProps<Schema extends z.ZodType<FilterSearchSchema, z.ZodTypeDef>> = DropdownCombinationType & {
  menuList: DropDownMenuItemProps[];
  className?: string;
  style?: React.CSSProperties;
  type: 'multiple-select' | 'single-select';
  options?: {
    isSearchable?: boolean;
    onSearchChange?: (search: string | null) => void;
    showCheckedOnTop?: boolean;
    onScrollEnd?: (menuRef: HTMLDivElement | null) => void;
    isLoading?: boolean;
    /**
     * @default true
     */
    clearSearchOnClose?: boolean;
    /**
     * used to pass filter bar state to the dropdown menu
     */
    filterBar?: z.infer<Schema>;
    filterBarKey?: keyof z.infer<Schema>;
    filterBarComparison?: (itemState: unknown, filterParam: unknown) => boolean | null | undefined;
  };
  hidden?: boolean;
};

type DropdownMenuItemState = {
  checked: boolean; // multiple-select mode
  selected: boolean; // single-select mode
  name?: string; // name of the item
  value?: unknown; // exact value of the item
  originalIndex: number; // original index of the item
  order: number; // order of the item
  hidden?: boolean; // used to hide the item
};

const filterSearch = (search: string | null, prev: DropdownMenuItemState[]): void => {
  if (!search || search === '') {
    prev.forEach((item) => {
      item.hidden = false;
    });

    return;
  }

  const items = matchSorter(prev, search, {
    keys: ['name'],
  });

  prev.forEach((item) => {
    if (item.checked === true) return;
    item.hidden = true;
  });

  items.forEach((item) => {
    prev[item.originalIndex].hidden = false;
  });
};

const sortBy = (type: 'checked' | 'originalIndex', prev: DropdownMenuItemState[]): void => {
  const sortedBy = prev.toSorted((a, b) => {
    if (a[type] === b[type]) return 0;
    if (type === 'checked') {
      if (a.checked === true && b.checked === false) return -1;
      return 1;
    }

    if (a.originalIndex > b.originalIndex) return 1;
    if (a.originalIndex < b.originalIndex) return -1;
    return 0;
  });

  sortedBy.forEach((item, idx) => {
    if (type === 'checked') {
      prev[item.originalIndex] = {
        ...item,
        order: idx + 1,
      };
    }
    if (type === 'originalIndex') {
      prev[idx] = {
        ...item,
        originalIndex: idx,
      };
    }
  });
};

export const DropdownMenu = <Schema extends z.ZodType<FilterSearchSchema, z.ZodTypeDef>>({
  type = 'single-select',
  ...props
}: DropDownMenuProps<Schema>) => {
  const [search, setSearch] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>();
  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const menuListRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<DropdownMenuItemState[]>(
    (props.menuList || []).map((list, idx) => ({
      ...(list?.name ? { name: list.name } : {}),
      ...(list?.value ? { value: list.value } : {}),
      order: idx + 1,
      originalIndex: idx,
      checked: list?.initValue && type === 'multiple-select' ? list.initValue : false,
      selected: list?.initValue && type === 'single-select' ? list.initValue : false,
    }))
  );

  // update the state when the menuList changes
  useEffect(() => {
    setState((prev) => {
      const newMenuItems = props.menuList || [];
      const prevLength = prev.length;

      if (newMenuItems.length > prevLength) {
        newMenuItems.forEach((item, idx) => {
          const existingItem = prev.find(
            (prevItem) =>
              prevItem.value === item.value ||
              props?.options?.filterBarComparison?.(prevItem.value, item.value) === true ||
              prevItem.name === item.name
          );

          if (!existingItem) {
            prev.push({
              ...(item?.name ? { name: item.name } : {}),
              ...(item?.value ? { value: item.value } : {}),
              order: prevLength + idx + 1,
              originalIndex: prevLength + idx,
              checked: item?.initValue && type === 'multiple-select' ? item.initValue : false,
              selected: item?.initValue && type === 'single-select' ? item.initValue : false,
            });
          }
        });
      } else if (newMenuItems.length < prevLength) {
        prev.forEach((item, idx) => {
          const existingItem = newMenuItems.find(
            (newItem) =>
              newItem.value === item.value ||
              props?.options?.filterBarComparison?.(newItem.value, item.value) === true ||
              newItem.name === item.name
          );

          if (!existingItem) {
            prev.splice(idx, 1);
          }
        });
      }

      sortBy('originalIndex', prev);
      if (props?.options?.showCheckedOnTop && type === 'multiple-select') {
        sortBy('checked', prev);
      }
      filterSearch(search, prev);

      return [...prev];
    });
  }, [props.menuList]);

  // update the state when the filterbar state changes
  useEffect(() => {
    if (!props?.options?.filterBar || !props.options.filterBarKey) return;
    const filterBarKey = props.options.filterBarKey;

    const { filterBar } = props.options;
    const filterParam = filterBar?.[filterBarKey];

    setState((prev) => {
      prev.forEach((item) => {
        if (!item?.value) return;

        const comparison = props?.options?.filterBarComparison?.(item.value, filterParam) || undefined;
        if (type === 'multiple-select') {
          if (typeof comparison === 'boolean') {
            item.checked = comparison;
          } else {
            item.checked = !!(filterParam as unknown[])?.includes?.(item.value);
          }
        } else {
          if (typeof comparison === 'boolean') {
            item.selected = comparison;
          } else {
            item.selected = !!filterParam ? item.value === filterParam : false;
          }
        }
      });

      if (props?.options?.showCheckedOnTop && type === 'multiple-select') {
        sortBy('checked', prev);
      }
      filterSearch(search, prev);

      return [...prev];
    });
  }, [props.options?.filterBar]);

  // handle scroll end event
  useEffect(() => {
    if (open === false) return;

    const handleScroll = (event: Event) => {
      if (!event.target) return;
      const { scrollHeight, scrollTop, clientHeight } = event.target as HTMLDivElement;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        props?.options?.onScrollEnd?.(menuListRef.current);
      }
    };

    // menuListRef takes a moment to be created once the menu is opened
    requestAnimationFrame(() => {
      menuListRef.current?.addEventListener('scroll', handleScroll);
    });

    return () => {
      menuListRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [open, menuListRef]);

  const onSearchChange = useCallback(
    (search: string | null) => {
      props?.options?.onSearchChange?.(search);
    },
    [props?.options?.onSearchChange]
  );

  const onSearchChangeDebounced = useCallback(debounce(onSearchChange, 500), []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);

    // reset the search input once the user closes the menu
    if (props?.options?.clearSearchOnClose === false || props?.options?.isSearchable !== true) return;
    setTimeout(() => {
      setSearch(null);
      onSearchChangeDebounced(null);

      setState((prev) => {
        prev.forEach((item) => {
          item.hidden = false;
        });

        return [...prev];
      });
    }, 250);
  }, [onSearchChangeDebounced, props?.options?.clearSearchOnClose, props?.options?.isSearchable]);

  const buttonClassName = useMemo(() => {
    if (!props?.className) return props?.name ? 'ZnglyDD-Btn-Text ZnglyBtnOutline ' : '';

    if (props.className === 'z-btn-6') return props.className;
    return (props?.name ? 'ZnglyDD-Btn-Text ZnglyBtnOutline ' : '') + props.className;
  }, [props?.className]);

  return (
    <>
      <Button
        style={props?.style}
        aria-controls={open ? 'basic-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        className={buttonClassName}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
      >
        <>
          {props?.icon}
          {props?.name && (
            <>
              {props.name}
              <ArrowDropDownIcon />
            </>
          )}
        </>
      </Button>
      <Menu
        className="DropDownMenu"
        anchorEl={anchorEl}
        hidden={props?.hidden}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            ref: menuListRef,
            style: {
              overflowY: 'auto',
              maxHeight: 450,
            },
          },
          list: {
            style: {
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {props?.options?.isLoading && (
          <LinearProgress
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 101,
              backgroundColor: 'white',
            }}
            style={{
              order: -1,
              width: '100%',
              height: '2px',
            }}
          />
        )}

        {/* Search Input */}
        {props?.options?.isSearchable && (
          <Text
            value={search || ''}
            debounceMs={750}
            fullWidth
            variant="standard"
            sx={{
              order: -1,
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: 'white',
              pt: 0.5,
            }}
            slotProps={{
              input: {
                sx: { p: '0 0 0 2em', width: '100%' },
                startAdornment: <FontAwesomeIcon style={{ marginRight: '0.5em' }} icon={faMagnifyingGlass} />,
                placeholder: 'Search',
                endAdornment: (
                  <FontAwesomeIcon
                    style={{
                      marginLeft: '0.5em',
                      paddingRight: '1em',
                      ...(search?.length
                        ? { cursor: 'pointer' }
                        : {
                            visibility: 'hidden',
                          }),
                    }}
                    icon={faXmark}
                    onClick={() => {
                      setSearch('');
                      onSearchChangeDebounced('');
                      setState((prev) => {
                        filterSearch(null, prev);
                        return [...prev];
                      });
                    }}
                  />
                ),
              },
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              onSearchChangeDebounced(e.target.value);
              setSearch(e.target.value);

              setState((prev) => {
                filterSearch(e.target.value, prev);

                return [...prev];
              });
            }}
          />
        )}

        {/* MenuItems */}
        {props.menuList.map(({ name, ...opt }, index) => {
          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
          if (opt?.divider) return <Divider key={`divider${name}${index}`} />;

          if (type === 'multiple-select')
            return (
              <MenuItem
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={name + index}
                value={name || ''}
                className="menu-item"
                style={{ order: state[index]?.order }}
                hidden={state[index]?.hidden === true}
              >
                <Checkbox
                  className="zn-chbx-1"
                  disabled={opt?.disabled}
                  checked={state[index]?.checked}
                  onClick={() => {
                    if (opt?.disabled) return;

                    setState((prev) => {
                      prev[index].checked = !prev[index].checked;

                      opt?.onClick?.(prev[index], menuListRef.current);

                      filterSearch(search, prev);

                      return [...prev];
                    });
                  }}
                />
                {opt?.icon && <ListItemIcon>{opt?.icon}</ListItemIcon>}
                <ListItemText primary={name} />
              </MenuItem>
            );

          return (
            <MenuItem
              value={name || ''}
              selected={state[index]?.selected}
              disabled={opt?.disabled}
              hidden={state[index]?.hidden === true}
              style={{ order: state[index]?.order }}
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              key={name + index}
              className={`menu-item ${opt?.customClass}`}
              onClick={() => {
                if (opt?.disabled) return;

                setState((prev) => {
                  prev.forEach((item) => {
                    item.selected = false;
                  });
                  prev[index].selected = true;

                  opt?.onClick?.(prev[index], menuListRef.current);

                  filterSearch(search, prev);

                  return [...prev];
                });

                setAnchorEl(null);
              }}
            >
              {opt?.icon && <ListItemIcon>{opt?.icon}</ListItemIcon>}
              <ListItemText primary={name} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
